import React, { useEffect, useMemo, useState } from 'react';
import ConfirmationModal from '../components/ConfirmationModal';
import { adminContactAPI } from '../services/api';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const AdminRequiries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  const totalPages = useMemo(() => {
    if (!total || !limit) return 1;
    return Math.max(1, Math.ceil(total / limit));
  }, [total, limit]);

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (email) params.email = email;
      if (name) params.name = name;
      const { data } = await adminContactAPI.list(params);
      const payload = data?.data || data;
      setItems(payload?.items || payload?.contacts || payload || []);
      setTotal(payload?.total || payload?.totalItems || payload?.pagination?.total || 0);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [page, limit]);

  const onApplyFilters = () => {
    setPage(1);
    fetchItems();
  };

  const onClearFilters = () => {
    setEmail('');
    setName('');
    setPage(1);
    fetchItems();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handleDelete = async (id) => {
    try {
      const res = await adminContactAPI.delete(id);
      if (res.data?.success) {
        setSuccess('Inquiry deleted');
        setTimeout(() => setSuccess(''), 2500);
        fetchItems();
      } else {
        setError(res.data?.message || 'Delete failed');
        setTimeout(() => setError(''), 2500);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
      setTimeout(() => setError(''), 2500);
    }
  };

  return (
    <div className="container">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ marginBottom: '0.5rem' }}>Requiries</h1>
        <p style={{ color: 'var(--gray-600)' }}>View and manage contact inquiries.</p>
      </div>

      {error && (<div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>)}
      {success && (<div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>)}

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-body">
          <div className="grid grid-cols-4" style={{ gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Search Email</label>
              <input
                type="text"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Filter by email"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Search Name</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Filter by name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Per Page</label>
              <select
                className="form-input"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option value={size} key={size}>{size}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ alignSelf: 'end' }}>
              <div className="d-flex" style={{ gap: '0.5rem' }}>
                <button onClick={onApplyFilters} disabled={loading} className="btn btn-primary">
                  Apply
                </button>
                <button onClick={onClearFilters} disabled={loading} className="btn btn-secondary">
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="d-flex justify-center align-center" style={{ minHeight: '300px' }}>
          <div className="spinner"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', opacity: 0.5, marginBottom: '0.5rem' }}>📨</div>
            <div style={{ color: 'var(--text-secondary)' }}>No inquiries</div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Phone</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Subject</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={it._id || idx}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{(page - 1) * limit + idx + 1}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{it.name || '-'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{it.email || '-'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{it.phone || '-'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{it.description || '-'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{formatDate(it.created_at || it.createdAt)}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>
                        <div className="d-flex" style={{ gap: '0.5rem' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={async () => {
                              try {
                                const res = await adminContactAPI.getOne(it._id);
                                const payload = res.data?.data || res.data;
                                setSelected(payload);
                              } catch (e) {
                                setError(e?.response?.data?.message || 'Failed to fetch inquiry');
                              }
                            }}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => {
                              setConfirmState({
                                isOpen: true,
                                title: 'Delete Inquiry',
                                message: 'Are you sure you want to delete this inquiry? This action cannot be undone.',
                                onConfirm: () => handleDelete(it._id),
                              });
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="card-footer">
              <div className="d-flex justify-between align-center">
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Page {page} of {totalPages} • {total} total
                </div>
                <div className="d-flex" style={{ gap: '0.5rem' }}>
                  <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</button>
                  <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Drawer/Modal for Single */}
      {selected && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '1.5rem'
        }}>
          <div style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--border-radius)',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0 }}>{selected.description || 'Inquiry'}</h3>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  {selected.name} • {selected.email}{selected.phone ? ` • ${selected.phone}` : ''} • {formatDate(selected.created_at || selected.createdAt)}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.25rem', cursor: 'pointer' }}>×</button>
            </div>
            <div className="card-body" style={{ overflow: 'auto' }}>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selected.message || '-'}</pre>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmState.onConfirm || (() => {})}
        title={confirmState.title}
        message={confirmState.message}
        confirmText="Delete"
        confirmButtonClass="btn btn-danger"
      />
    </div>
  );
};

export default AdminRequiries;


